// WorkspaceSwitcher — dropdown surfacing all workspaces + a "+ New" entry.
//
// Why a dedicated component:
//   - The workspace concept is plural; users WILL want to compare a
//     "buy a 3BR" vs "rent for 1 year" search side-by-side without
//     polluting one another. The default workspace was meant as the
//     happy-path entry, not a hard cap.
//   - Routing already accepts /workspace/:wsId, so switcher is purely
//     a navigation surface; no schema change needed.
//
// Display rule (B4): we render the user-set `name` if non-empty,
// otherwise show "Workspace #N" so anonymized internal names (e.g.
// `default-${user_id}`) never reach the UI. The backend still owns
// the canonical name.
//
// New workspace: opens an inline modal asking for a name; on success
// we navigate to the new wsId. We deliberately don't auto-name —
// users get value from a meaningful label here.

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { listWorkspaces, createWorkspace, type WsWorkspace } from "@/lib/workspaceApi";
import { TOUR_ANCHORS } from "@/lib/tour-anchors";

function displayName(w: WsWorkspace, idx: number): string {
  const raw = (w.name ?? "").trim();
  // Hide internal "default-<uuid>" / "ws-<uuid>" style names that the
  // backend may auto-generate. Anything that matches our internal
  // prefix or is empty falls back to a numbered label.
  if (!raw || /^default-/i.test(raw) || /^ws-/i.test(raw)) {
    return `Workspace #${idx + 1}`;
  }
  return raw;
}

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { wsId } = useParams<{ wsId: string }>();
  const [items, setItems] = useState<WsWorkspace[]>([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const refresh = async () => {
    try {
      const list = await listWorkspaces();
      setItems(list);
    } catch {
      // Best-effort.
    }
  };

  useEffect(() => { refresh(); }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Esc closes whichever surface is on top — modal first, then dropdown.
  useEffect(() => {
    if (!open && !creating) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (creating) setCreating(false);
        else setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, creating]);

  const current = items.find((w) => w.id === wsId) ?? items[0];
  const currentLabel = current
    ? displayName(current, items.findIndex((w) => w.id === current.id))
    : t("workspace.switcher.label");

  const onCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setCreateErr(null);
    try {
      const ws = await createWorkspace(name, "buy");
      setItems((prev) => [...prev, ws]);
      setCreating(false);
      setNewName("");
      navigate(`/workspace/${ws.id}`);
    } catch {
      setCreateErr(t("workspace.switcher.createError"));
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div ref={rootRef} className="relative" data-tour-id={TOUR_ANCHORS.workspaceSwitcher}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[12px] font-medium text-text hover:bg-surface-2"
      >
        <span className="text-[10px] uppercase tracking-widest text-text-muted">
          {t("workspace.switcher.label")}
        </span>
        <span className="max-w-[160px] truncate">{currentLabel}</span>
        <span aria-hidden className="text-text-muted">▾</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[220px] rounded-md border border-border bg-surface py-1 shadow-lg"
        >
          {items.map((w, idx) => {
            const active = w.id === current?.id;
            return (
              <button
                key={w.id}
                role="option"
                aria-selected={active}
                onClick={() => {
                  setOpen(false);
                  navigate(`/workspace/${w.id}`);
                }}
                className={
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] " +
                  // Active row: 50-tint band + 900 ink reads on a light bg
                  // (the previous /15 + emerald-100 was unreadable on the
                  // consumer cream surface).
                  (active ? "bg-emerald-50 text-emerald-900" : "text-text hover:bg-surface-2")
                }
              >
                <span className="truncate">{displayName(w, idx)}</span>
                {active && <span aria-hidden className="ml-auto text-emerald-600">✓</span>}
              </button>
            );
          })}
          <div className="my-1 border-t border-border" />
          <button
            onClick={() => {
              setOpen(false);
              setCreating(true);
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12px] text-text hover:bg-surface-2"
          >
            {t("workspace.switcher.newWorkspace")}
          </button>
        </div>
      )}
      {creating && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setCreating(false); }}
        >
          <div className="rounded-xl border border-border bg-surface p-5 text-text" style={{ maxWidth: 420, width: "100%" }}>
            <h3 className="m-0 mb-1 text-[16px] font-semibold">{t("workspace.switcher.createTitle")}</h3>
            <p className="mt-0 mb-3 text-[12px] text-text-muted">{t("workspace.switcher.createBody")}</p>
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") onCreate(); }}
              placeholder={t("workspace.switcher.namePlaceholder") as string}
              className="w-full rounded-md border border-border bg-surface-2 px-3 py-2 text-[13px] text-text outline-none focus:border-emerald-500"
            />
            {createErr && (
              <div className="mt-2 text-[12px] text-red-300">{createErr}</div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setCreating(false)}
                className="rounded-md border border-border bg-surface-2 px-3 py-1.5 text-[12px] text-text-sec hover:bg-surface-3"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={onCreate}
                disabled={busy || !newName.trim()}
                className="rounded-md bg-emerald-500 px-3.5 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50"
              >
                {t("workspace.switcher.create")}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
